// app/lib/utils.ts
"use client"

import departmentsMap from '@/data/departments.json';
import { normalizeRutgersBaseCourseId } from './courseIdFormatter';

// Utility function to combine multiple class names.
// It takes any number of strings, booleans, or undefined values and returns a single string with all valid class names.
export function cn(...classes: (string | boolean | undefined)[]) {
  // Filter removes any falsey values and join creates one space-separated string.
  return classes.filter(Boolean).join(" ")
}

// Department helpers
export function getDepartmentName(departmentNumber: string | undefined | null): string | null {
  if (!departmentNumber) return null;
  const key = departmentNumber.toString().padStart(3, '0').slice(-3);
  return (departmentsMap as Record<string, string>)[key] ?? null;
}

export function formatDepartmentDisplay(departmentNumber: string | undefined | null): string {
  if (!departmentNumber) return 'Unknown';
  const name = getDepartmentName(departmentNumber);
  return name ? `${departmentNumber} (${name})` : departmentNumber;
}

// Format base course ID (e.g., 01198111) to include colons: 01:198:111
export function formatBaseIdDisplay(value: string | number | undefined | null): string {
  if (value === undefined || value === null) return '';
  const raw = String(value);
  return normalizeRutgersBaseCourseId(raw) ?? raw;
}

// Graduate-level course numbers start at 500.
export function isGradCourse(courseId: string | undefined | null, courseNumber?: string | number | undefined | null): boolean {
  if (!courseId && !courseNumber) return false;
  
  // Try to extract course number from courseId if courseNumber not provided
  let numToCheck = courseNumber ? String(courseNumber) : null;
  
  if (!numToCheck && courseId) {
    const raw = String(courseId);
    // Handle formats like "01:198:500" or "01198500"
    if (raw.includes(':')) {
      const parts = raw.split(':');
      numToCheck = parts[parts.length - 1]; // Get last part (course number)
    } else {
      const digits = raw.replace(/\D/g, '');
      if (digits.length >= 8) {
        numToCheck = digits.slice(5, 8); // Extract course number
      }
    }
  }
  
  if (!numToCheck) return false;
  
  // Remove any leading zeros and parse
  const courseNum = parseInt(numToCheck, 10);
  return !isNaN(courseNum) && courseNum >= 500;
}

// Get styling classes for grad courses badge
export function getGradCourseBadge(): string {
  return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 text-xs font-semibold px-2 py-0.5 rounded';
}

// Get border styling for grad courses
export function getGradCourseBorder(): string {
  return 'border-l-4 border-l-purple-500 dark:border-l-purple-400';
}

// Fuzzy matching utilities for smarter course search

/**
 * Calculate Levenshtein distance between two strings
 */
function levenshteinDistance(str1: string, str2: string): number {
  const len1 = str1.length;
  const len2 = str2.length;
  const matrix: number[][] = [];

  // Initialize matrix
  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }

  // Fill matrix
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,      // deletion
        matrix[i][j - 1] + 1,      // insertion
        matrix[i - 1][j - 1] + cost // substitution
      );
    }
  }

  return matrix[len1][len2];
}

/**
 * Calculate similarity score between two strings (0-1, where 1 is identical)
 */
export function calculateSimilarity(str1: string, str2: string): number {
  if (str1 === str2) return 1;
  if (!str1 || !str2) return 0;
  
  const maxLen = Math.max(str1.length, str2.length);
  if (maxLen === 0) return 1;
  
  const distance = levenshteinDistance(str1.toLowerCase(), str2.toLowerCase());
  return 1 - (distance / maxLen);
}

/**
 * Check if query words partially match in target string (word-by-word matching)
 */
export function partialMatch(query: string, target: string): boolean {
  if (!query || !target) return false;
  
  const queryWords = query.toLowerCase().trim().split(/\s+/);
  const targetLower = target.toLowerCase();
  
  // Check if all query words appear in the target (in any order)
  return queryWords.every(word => {
    // For very short words (1-2 chars), require exact match or as substring
    if (word.length <= 2) {
      return targetLower.includes(word);
    }
    // For longer words, allow fuzzy matching with some tolerance
    return targetLower.includes(word) || 
           targetLower.split(/\s+/).some(targetWord => 
             calculateSimilarity(word, targetWord) > 0.7
           );
  });
}

/**
 * Fuzzy match score for a query against multiple target fields
 * Returns a score from 0-1 indicating how well the query matches
 */
export function fuzzyMatchScore(
  query: string, 
  targets: string[], 
  options?: { threshold?: number }
): number {
  if (!query || targets.length === 0) return 0;
  
  const threshold = options?.threshold ?? 0.6;
  const queryLower = query.toLowerCase().trim();
  
  // Try direct substring matches first (highest priority)
  for (const target of targets) {
    const targetLower = target.toLowerCase();
    if (targetLower.includes(queryLower)) {
      return 1.0; // Perfect match
    }
  }
  
  // Try fuzzy matching
  let maxSimilarity = 0;
  for (const target of targets) {
    const targetLower = target.toLowerCase();
    
    // Calculate similarity for full strings
    const fullSimilarity = calculateSimilarity(queryLower, targetLower);
    
    // Calculate similarity for individual words
    const queryWords = queryLower.split(/\s+/);
    const targetWords = targetLower.split(/\s+/);
    
    let wordSimilarity = 0;
    for (const qWord of queryWords) {
      let bestMatch = 0;
      for (const tWord of targetWords) {
        const sim = calculateSimilarity(qWord, tWord);
        if (sim > bestMatch) bestMatch = sim;
      }
      wordSimilarity += bestMatch;
    }
    wordSimilarity = queryWords.length > 0 ? wordSimilarity / queryWords.length : 0;
    
    maxSimilarity = Math.max(maxSimilarity, fullSimilarity, wordSimilarity);
  }
  
  // Only return if above threshold
  return maxSimilarity >= threshold ? maxSimilarity : 0;
}

/**
 * Debounce function to limit how often a function is called
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };
    
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Convert instructor name from "LAST, FIRST" format to "First Last" title case
 * Handles various formats: "LAST, FIRST", "First Last", "LAST FIRST", etc.
 */
export function formatInstructorNameToTitleCase(rawName: string): string {
  if (!rawName || rawName === 'TBA') return rawName;
  
  const trimmed = rawName.trim();
  
  // Check if name contains a comma (Last, First format)
  if (trimmed.includes(',')) {
    const [last, first] = trimmed.split(',').map(part => part.trim());
    if (first && last) {
      return `${capitalizeWords(first)} ${capitalizeWords(last)}`;
    }
    return capitalizeWords(last || first);
  }
  
  // Split by spaces and capitalize each word
  return capitalizeWords(trimmed);
}

/**
 * Capitalize words in a string (title case)
 */
function capitalizeWords(str: string): string {
  return str
    .split(' ')
    .map(word => {
      if (!word) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Check if two names are similar enough to be considered the same person
 * Used for detecting name mismatches between raw data and matched sources
 */
export function namesMatch(name1: string, name2: string): boolean {
  if (!name1 || !name2) return false;
  
  const normalize = (name: string) => name.toLowerCase().replace(/[.,]/g, '').trim();
  const n1 = normalize(name1);
  const n2 = normalize(name2);
  
  // Exact match
  if (n1 === n2) return true;
  
  // Extract last names (typically more reliable for matching)
  const getLastName = (name: string) => {
    const parts = name.split(/[,\s]+/);
    // If comma-separated, first part is last name
    if (name.includes(',')) return parts[0];
    // Otherwise, last part is typically last name
    return parts[parts.length - 1];
  };
  
  const lastName1 = normalize(getLastName(name1));
  const lastName2 = normalize(getLastName(name2));
  
  // Last names must match
  if (lastName1 !== lastName2) return false;
  
  // Check if first names/initials are similar
  const getFirstName = (name: string) => {
    const parts = name.split(/[,\s]+/);
    if (name.includes(',')) return parts[1] || '';
    return parts[0] || '';
  };
  
  const firstName1 = normalize(getFirstName(name1));
  const firstName2 = normalize(getFirstName(name2));
  
  // If either first name is empty or very short, consider it a match if last names match
  if (!firstName1 || !firstName2 || firstName1.length <= 1 || firstName2.length <= 1) {
    return true;
  }
  
  // Check if first names start with the same letter or are similar
  if (firstName1[0] === firstName2[0]) return true;
  
  // Use fuzzy matching for first names
  const similarity = calculateSimilarity(firstName1, firstName2);
  return similarity > 0.7;
}

